import React, { useState, useEffect } from 'react';
import { paiementService } from '../services/api';
import Navbar from '../components/Navbar';
import { LockClosedIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { notify } from '../components/NotificationContainer';

const GestionHelloAsso = () => {
    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otpCode, setOtpCode] = useState('');
    const [editToken, setEditToken] = useState(null);
    const [formData, setFormData] = useState({
        client_id: '',
        client_secret: '',
        organization_slug: '',
        sandbox_mode: true
    });
    const [otpStep, setOtpStep] = useState(1); // 1: Request, 2: Verify

    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        setLoading(true);
        try {
            const response = await paiementService.configHelloAsso.get();
            setConfig(response.data);
            // Initialiser le formulaire avec les valeurs (sauf secret bien sûr)
            setFormData({
                client_id: '',
                client_secret: '',
                organization_slug: response.data.organization_slug || '',
                sandbox_mode: response.data.has_config ? response.data.sandbox_mode : true
            });
        } catch (error) {
            console.error(error);
            notify.error("Impossible de charger la configuration");
        } finally {
            setLoading(false);
        }
    };

    const handleRequestEdit = async () => {
        setLoading(true);
        try {
            await paiementService.configHelloAsso.requestEdit();
            setOtpStep(2);
            setShowOtpModal(true);
            notify.success("Code de vérification envoyé par email");
        } catch (error) {
            console.error(error);
            notify.error("Erreur lors de la demande de modification");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if (!otpCode) return notify.error("Veuillez saisir le code");

        setLoading(true);
        try {
            const response = await paiementService.configHelloAsso.verifyEdit(otpCode);
            setEditToken(response.data.edit_token);
            setShowOtpModal(false);
            setEditMode(true);
            setOtpCode('');
            setOtpStep(1);
            notify.success("Accès autorisé");
        } catch (error) {
            const msg = error.response?.data?.error || "Code invalide";
            notify.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.client_id || !formData.client_secret || !formData.organization_slug) {
            return notify.error("Tous les champs sont requis");
        }

        setLoading(true);
        try {
            await paiementService.configHelloAsso.update({
                ...formData,
                edit_token: editToken
            });
            notify.success("Configuration mise à jour avec succès");
            setEditMode(false);
            setEditToken(null);
            fetchConfig();
        } catch (error) {
            console.error(error);
            notify.error(error.response?.data?.error || "Erreur lors de la sauvegarde");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">

                <div className="md:flex md:items-center md:justify-between mb-8">
                    <div className="flex-1 min-w-0">
                        <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                            Configuration HelloAsso
                        </h2>
                    </div>
                </div>

                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                    <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                        <div>
                            <h3 className="text-lg leading-6 font-medium text-gray-900">
                                Paramètres API
                            </h3>
                            <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                Gestion des clés d'API et de l'environnement.
                            </p>
                        </div>
                        {!editMode && (
                            <button
                                onClick={handleRequestEdit}
                                disabled={loading}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <LockClosedIcon className="h-5 w-5 mr-2" />
                                {loading ? 'Envoi en cours...' : 'Modifier'}
                            </button>
                        )}
                    </div>

                    {!editMode ? (
                        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
                            <dl className="sm:divide-y sm:divide-gray-200">
                                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-sm font-medium text-gray-500">Client ID</dt>
                                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {config?.client_id || <span className="text-gray-400 italic">Non configuré</span>}
                                    </dd>
                                </div>
                                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-sm font-medium text-gray-500">Organization Slug</dt>
                                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {config?.organization_slug || <span className="text-gray-400 italic">Non configuré</span>}
                                    </dd>
                                </div>
                                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-sm font-medium text-gray-500">Environnement</dt>
                                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.sandbox_mode ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                            {config?.sandbox_mode ? 'SANDBOX (Test)' : 'PRODUCTION'}
                                        </span>
                                    </dd>
                                </div>
                                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                                    <dt className="text-sm font-medium text-gray-500">Dernière mise à jour</dt>
                                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                        {config?.updated_at ? new Date(config.updated_at).toLocaleDateString('fr-FR') : '-'}
                                        {config?.updated_by && ` par ${config.updated_by}`}
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    ) : (
                        <form onSubmit={handleSave} className="border-t border-gray-200 px-4 py-5 sm:px-6">
                            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                                <div className="sm:col-span-6">
                                    <div className="rounded-md bg-blue-50 p-4 mb-4">
                                        <div className="flex">
                                            <div className="flex-shrink-0">
                                                <ShieldCheckIcon className="h-5 w-5 text-blue-400" aria-hidden="true" />
                                            </div>
                                            <div className="ml-3 flex-1 md:flex md:justify-between">
                                                <p className="text-sm text-blue-700">
                                                    Mode édition sécurisé activé. Vous pouvez modifier les identifiants.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="sm:col-span-4">
                                    <label htmlFor="client_id" className="block text-sm font-medium text-gray-700">
                                        Client ID
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            type="text"
                                            name="client_id"
                                            id="client_id"
                                            value={formData.client_id}
                                            onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="sm:col-span-4">
                                    <label htmlFor="client_secret" className="block text-sm font-medium text-gray-700">
                                        Client Secret
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            type="password"
                                            name="client_secret"
                                            id="client_secret"
                                            value={formData.client_secret}
                                            onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
                                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="sm:col-span-4">
                                    <label htmlFor="organization_slug" className="block text-sm font-medium text-gray-700">
                                        Organization Slug
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            type="text"
                                            name="organization_slug"
                                            id="organization_slug"
                                            value={formData.organization_slug}
                                            onChange={(e) => setFormData({ ...formData, organization_slug: e.target.value })}
                                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="sm:col-span-4">
                                    <div className="relative flex items-start">
                                        <div className="flex items-center h-5">
                                            <input
                                                id="sandbox_mode"
                                                name="sandbox_mode"
                                                type="checkbox"
                                                checked={formData.sandbox_mode}
                                                onChange={(e) => setFormData({ ...formData, sandbox_mode: e.target.checked })}
                                                className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                                            />
                                        </div>
                                        <div className="ml-3 text-sm">
                                            <label htmlFor="sandbox_mode" className="font-medium text-gray-700">
                                                Mode Sandbox (Test)
                                            </label>
                                            <p className="text-gray-500">Décochez pour passer en production réelle.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-5">
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => setEditMode(false)}
                                        className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="submit"
                                        className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                    >
                                        Enregistrer
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            {/* Modal OTP */}
            {showOtpModal && (
                <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowOtpModal(false)}></div>

                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6">
                            <div>
                                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                                    <LockClosedIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                                </div>
                                <div className="mt-3 text-center sm:mt-5">
                                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                        Vérification de sécurité
                                    </h3>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500">
                                            Un code temporaire a été envoyé à votre adresse email. Veuillez le saisir ci-dessous pour modifier la configuration.
                                        </p>
                                    </div>
                                    <div className="mt-4">
                                        <input
                                            type="text"
                                            className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md text-center text-xl tracking-widest"
                                            placeholder="123456"
                                            maxLength={6}
                                            value={otpCode}
                                            onChange={(e) => setOtpCode(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5 sm:mt-6">
                                <button
                                    type="button"
                                    className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:text-sm"
                                    onClick={handleVerifyOtp}
                                >
                                    Vérifier
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GestionHelloAsso;
